// 盤點模式 — make ONE field (上架 or 櫃位 X) match the cabinet by scanning it.
//
// WHY THIS IS NOT 掃碼上架. ScanShelveDialog moves a PIECE per scan (辦公室 −1 /
// 在櫃 +1). A 盤點 moves nothing: it answers 「A1 上到底有哪些款」 and rewrites the
// record to match. Barcode = variant, so a second scan of the same tag only bumps
// the local ×N — it is still one variant on the shelf.
//
// Two modes:
//   全部重盤 — scans collect locally (persisted per target, so a phone reload loses
//     nothing), then a server dry-run review lists what will be set and what will
//     be CLEARED because it was not scanned. Only the rows the staffer leaves
//     ticked on that screen are sent as remove_ids; the server clears nothing else.
//   只新增 — every scan saves immediately (one session per scan, undoable per line)
//     and nothing is ever cleared.
//
// The only quantity change: a scanned piece the system shows at 在櫃 <= 0 is
// evidently here, so the server corrects the count on commit — takes it from
// 辦公室, and 補登 whatever the office count can't cover (Magnus 2026-09-30: no
// per-line question, just fix it). The line and review screen say what happens.
//
// A full 重盤 with nothing scanned would clear the whole scope, so the check
// button is disabled at 0 lines; an empty shelf goes through its own
// 「這個櫃位是空的」 review with an explicit 確定清空 (server: confirm_empty).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import api from '../../services/api';
import { NOKE_SLOTS, normalizeSlot, parseSlotBarcode } from '../../utils/nokeSlots';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import type { ScanInventoryItem } from './ScanShelveDialog';

export type ReconcileTarget = 'display' | 'slot';
type ReconcileMode = 'full' | 'add';
type LineStatus = 'set' | 'already' | 'moved_from' | 'need_office_move' | 'blocked';
type Phase = 'setup' | 'scan' | 'review' | 'done';

interface ReconcileItem extends ScanInventoryItem {
  product_title: string;
  from_slot?: string | null;
  moved_qty?: number;
  /** Server plan for a need_office_move line: pieces taken from 辦公室 / 補登. */
  move_qty?: number;
  found_qty?: number;
}

/** 「從辦公室轉入 2 件・補登 1 件」 for a count correction; '' when there is none. */
const stockFixText = (it: { move_qty?: number; found_qty?: number; moved_qty?: number }) => {
  const move = it.move_qty ?? it.moved_qty ?? 0;
  const found = it.found_qty ?? 0;
  return [move > 0 ? `從辦公室轉入 ${move} 件` : '', found > 0 ? `補登 ${found} 件` : ''].filter(Boolean).join('・');
};

interface Resolution { code: string; status: 'ok' | 'unknown' | 'ambiguous'; variant_id: number | null }

interface DryRun {
  resolutions: Resolution[];
  set: ReconcileItem[];
  already: ReconcileItem[];
  moved_from: ReconcileItem[];
  need_office_move: ReconcileItem[];
  blocked: ReconcileItem[];
  remove_candidates: ReconcileItem[];
  unknown: string[];
  ambiguous: string[];
}

interface CommitResult {
  session_id: number;
  set: ReconcileItem[];
  already: ReconcileItem[];
  moved_from: ReconcileItem[];
  not_moved: ReconcileItem[];
  blocked: ReconcileItem[];
  removed: ReconcileItem[];
  remove_skipped: Array<Partial<ReconcileItem> & { variant_id: number }>;
  changed_count: number;
}

interface UndoResult { restored: number[]; skipped: Array<{ variant_id: number; reason: string }> }

interface Session { target: ReconcileTarget; slot: string | null; mode: ReconcileMode }

interface Line {
  key: string;
  variant_id: number;
  /** First raw code — the one sent to the server; `codes` also holds aliases. */
  code: string;
  codes: string[];
  count: number;
  status: LineStatus;
  item: ReconcileItem;
  fromSlot: string | null;
  /** 轉入門市 1 件 — only meaningful for need_office_move; default checked. */
  moveOffice: boolean;
  /** 只新增: the saved session behind this line, for undo. */
  sessionId?: number | null;
  undone?: boolean;
}

interface Draft { target: ReconcileTarget; slot: string | null; lines: Line[]; savedAt: number }

interface Props {
  open: boolean;
  /** changed = at least one commit/undo landed, so the tab should refetch. */
  onClose: (changed: boolean) => void;
}

// A wedge scanner can double-fire on one trigger pull; a second read of the same
// code inside this window is dropped. Harmless either way (it would only bump ×N).
const SAME_CODE_MS = 1500;
const BUCKETS: LineStatus[] = ['set', 'already', 'moved_from', 'need_office_move', 'blocked'];

const newKey = () => `rc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const errorText = (err: any, fallback: string): string =>
  err?.response?.data?.error?.message || err?.response?.data?.message || fallback;
const variantLabel = (it: { sku?: string; color?: string; size?: string }) =>
  [it.sku, [it.color, it.size].filter(Boolean).join(' / ')].filter(Boolean).join(' · ');
const targetLabel = (s: { target: ReconcileTarget; slot: string | null }) =>
  (s.target === 'display' ? '上架' : `櫃位 ${s.slot}`);

// ---- draft storage (full mode only) -----------------------------------------
// Each access is guarded: private mode / blocked storage throws, and a 盤點 must
// still work — it just won't survive a reload.
const DRAFT_PREFIX = 'noke_reconcile_draft:';
const draftKey = (target: ReconcileTarget, slot: string | null) =>
  `${DRAFT_PREFIX}${target === 'display' ? 'display' : `slot:${slot}`}`;

function storageGet(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function storageSet(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* storage unavailable */ }
}
function storageRemove(key: string): void {
  try { window.localStorage.removeItem(key); } catch { /* storage unavailable */ }
}
function readDraft(key: string): Draft | null {
  const raw = storageGet(key);
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Draft;
    if (!d || typeof d !== 'object' || !Array.isArray(d.lines) || d.lines.length === 0) throw new Error('shape');
    if (d.target !== 'display' && d.target !== 'slot') throw new Error('shape');
    if (d.target === 'slot' && typeof d.slot !== 'string') throw new Error('shape');
    // Every line is rendered and sent; one bad line (older app version, hand-
    // edited storage) would crash the dialog, so the whole draft is dropped.
    const ok = d.lines.every((l) => l && typeof l === 'object' && typeof l.key === 'string'
      && typeof l.code === 'string' && Array.isArray(l.codes) && typeof l.count === 'number'
      && typeof l.variant_id === 'number' && BUCKETS.includes(l.status)
      && l.item && typeof l.item === 'object' && typeof l.item.variant_id === 'number' && Number.isFinite(l.item.variant_id));
    if (!ok) throw new Error('shape');
    return { ...d, savedAt: typeof d.savedAt === 'number' ? d.savedAt : 0 };
  } catch {
    storageRemove(key);
    return null;
  }
}
function listDrafts(): Draft[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k);
    }
  } catch { return []; }
  return keys.map(readDraft).filter((d): d is Draft => d != null).sort((a, b) => b.savedAt - a.savedAt);
}

// ---- feedback ---------------------------------------------------------------
// The staffer is looking at the shelf, not the phone: ok = one high blip,
// warning = two-tone (moved from another slot / office stock), error = low buzz.
let audioCtx: AudioContext | null = null;
function getAudio(): AudioContext | null {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx || audioCtx.state === 'closed') audioCtx = new Ctx() as AudioContext;
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    return audioCtx;
  } catch { return null; }
}
function tone(ctx: AudioContext, freq: number, start: number, dur: number, type: OscillatorType): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.value = 0.15;
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(ctx.currentTime + start);
  osc.stop(ctx.currentTime + start + dur);
}
function feedback(kind: 'ok' | 'warn' | 'error'): void {
  const ctx = getAudio();
  if (ctx) {
    try {
      if (kind === 'ok') tone(ctx, 1175, 0, 0.09, 'sine');
      else if (kind === 'warn') { tone(ctx, 660, 0, 0.12, 'triangle'); tone(ctx, 990, 0.15, 0.12, 'triangle'); }
      else tone(ctx, 150, 0, 0.35, 'sawtooth');
    } catch { /* audio unavailable — non-fatal */ }
  }
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    try { navigator.vibrate(kind === 'ok' ? 60 : kind === 'warn' ? [80, 60, 80] : [220, 80, 220]); } catch { /* non-fatal */ }
  }
}

// A tapped checkbox keeps focus, and it is an <input>: useBarcodeScanner then
// treats every scanner burst as typing into it and drops the scan. Hand focus
// back to the page after each tap so the next hang tag still registers.
const releaseFocus = () => {
  const el = document.activeElement as HTMLElement | null;
  if (el && typeof el.blur === 'function') el.blur();
};

function findStatus(d: DryRun, variantId: number): { status: LineStatus; item: ReconcileItem } | null {
  for (const status of BUCKETS) {
    const item = d[status].find((i) => i.variant_id === variantId);
    if (item) return { status, item };
  }
  return null;
}

const Thumb: React.FC<{ src?: string | null; size?: number }> = ({ src, size = 48 }) => (
  <Avatar variant="rounded" src={src || undefined} sx={{ width: size, height: size, bgcolor: 'action.hover', color: 'text.disabled' }}>
    <ImageOutlinedIcon fontSize="small" />
  </Avatar>
);

/** Compact row for the review/result lists. */
const ItemRow: React.FC<{ item: Partial<ReconcileItem>; extra?: React.ReactNode; action?: React.ReactNode }> = ({ item, extra, action }) => (
  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 0.75, minWidth: 0 }}>
    <Thumb src={item.image_url} size={40} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>{variantLabel(item)}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap component="div">
        在櫃 {item.qty_on_hand ?? 0}{(item.qty_office ?? 0) > 0 ? ` · 辦公室 ${item.qty_office}` : ''}
        {extra ? ' · ' : ''}{extra}
      </Typography>
    </Box>
    {action}
  </Stack>
);

const Section: React.FC<{ title: string; count: number; color?: string; children?: React.ReactNode }> = ({ title, count, color, children }) => (
  count === 0 ? null : (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color }}>{title}（{count}）</Typography>
      <Divider sx={{ mb: 0.5 }} />
      {children}
    </Box>
  )
);

export default function ReconcileScanDialog({ open, onClose }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Setup inputs.
  const [target, setTarget] = useState<ReconcileTarget>('slot');
  const [slotInput, setSlotInput] = useState('');
  const [mode, setMode] = useState<ReconcileMode>('full');
  const [drafts, setDrafts] = useState<Draft[]>([]);

  // Active session. Mirrored into refs because scans are processed on an async
  // queue that must read the CURRENT session/lines, not a render's snapshot.
  const [phase, setPhase] = useState<Phase>('setup');
  const [session, setSessionState] = useState<Session | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const setSession = (s: Session | null) => { sessionRef.current = s; setSessionState(s); };
  const [lines, setLinesState] = useState<Line[]>([]);
  const linesRef = useRef<Line[]>([]);
  const setLines = useCallback((next: Line[] | ((prev: Line[]) => Line[])) => {
    const value = typeof next === 'function' ? next(linesRef.current) : next;
    linesRef.current = value;
    setLinesState(value);
  }, []);

  const [message, setMessage] = useState<{ severity: 'success' | 'info' | 'warning' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState('');
  const [switchTo, setSwitchTo] = useState<string | null>(null);
  const [pendingSlot, setPendingSlot] = useState<string | null>(null);
  const [review, setReview] = useState<DryRun | null>(null);
  /** Review opened from 「這個櫃位是空的」: commits with confirm_empty. */
  const [emptyReview, setEmptyReview] = useState(false);
  /** Remove candidates the staffer unticked (= keep). */
  const [keepIds, setKeepIds] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<CommitResult | null>(null);
  const [undoResult, setUndoResult] = useState<UndoResult | null>(null);

  const changedRef = useRef(false);
  // One client_ref per distinct commit payload: a retry after a dropped phone
  // connection replays server-side instead of applying twice.
  const commitRef = useRef<{ ref: string; payload: string } | null>(null);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const lastScan = useRef<{ code: string; at: number }>({ code: '', at: 0 });

  useEffect(() => {
    if (!open) return;
    setPhase('setup');
    setSession(null);
    setLines([]);
    setMessage(null);
    setReview(null);
    setResult(null);
    setUndoResult(null);
    setPendingSlot(null);
    setSwitchTo(null);
    setManual('');
    changedRef.current = false;
    commitRef.current = null;
    setDrafts(listDrafts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Full-mode draft follows every change to the scanned list.
  useEffect(() => {
    if (!session || session.mode !== 'full' || (phase !== 'scan' && phase !== 'review')) return;
    const key = draftKey(session.target, session.slot);
    if (lines.length === 0) storageRemove(key);
    else storageSet(key, JSON.stringify({ target: session.target, slot: session.slot, lines, savedAt: Date.now() }));
  }, [lines, session, phase]);

  const startSession = useCallback((t: ReconcileTarget, s: string | null, m: ReconcileMode, resume: boolean) => {
    const sess: Session = { target: t, slot: t === 'slot' ? s : null, mode: m };
    const key = draftKey(sess.target, sess.slot);
    const draft = m === 'full' && resume ? readDraft(key) : null;
    if (m === 'full' && !resume) storageRemove(key);
    setSession(sess);
    setLines(draft ? draft.lines : []);
    setReview(null);
    setResult(null);
    setUndoResult(null);
    setKeepIds(new Set());
    commitRef.current = null;
    setMessage(draft ? { severity: 'info', text: `已載入「${targetLabel(sess)}」上次未完成的 ${draft.lines.length} 款` } : null);
    setPhase('scan');
  }, [setLines]);

  // ---- scanning -------------------------------------------------------------
  const commitAdd = async (sess: Session, line: Line): Promise<CommitResult> => {
    const res = await api.post('/api/noke/inventory/reconcile', {
      target: sess.target,
      slot: sess.slot ?? undefined,
      mode: 'add',
      codes: [line.code],
      move_from_office_ids: line.status === 'need_office_move' ? [line.variant_id] : [],
      dry_run: false,
      client_ref: newKey(),
    });
    return res.data as CommitResult;
  };

  const processCode = async (code: string) => {
    const sess = sessionRef.current;
    if (!sess) return;
    // Tag barcodes are plain ASCII. Anything else means the phone's keyboard
    // layout (注音) rewrote the scanner's keys — e.g. it went into a focused field.
    if (/[^\x20-\x7e]/.test(code)) {
      setMessage({ severity: 'error', text: `條碼被手機鍵盤改成「${code}」：請把鍵盤切換成英文（ABC），或先點空白處再掃` });
      feedback('error');
      return;
    }
    const bump = (key: string, alias?: string) => setLines((prev) => prev.map((l) => (l.key === key
      ? { ...l, count: l.count + 1, codes: alias && !l.codes.includes(alias) ? [...l.codes, alias] : l.codes }
      : l)));

    const known = linesRef.current.find((l) => !l.undone && l.codes.includes(code));
    if (known) {
      bump(known.key);
      setMessage({ severity: 'info', text: `${variantLabel(known.item)} ×${known.count + 1}（同一款只算一次）` });
      feedback(known.status === 'blocked' ? 'error' : 'ok');
      return;
    }

    let dry: DryRun;
    try {
      dry = (await api.post('/api/noke/inventory/reconcile', {
        target: sess.target, slot: sess.slot ?? undefined, mode: 'add', codes: [code], dry_run: true,
      })).data;
    } catch (err: any) {
      setMessage({ severity: 'error', text: errorText(err, '查詢條碼失敗，請再掃一次') });
      feedback('error');
      return;
    }
    if (sessionRef.current !== sess) return; // session switched while in flight

    if (dry.ambiguous.length) {
      setMessage({ severity: 'error', text: `${code}：條碼不明確，請掃商品吊牌` });
      feedback('error');
      return;
    }
    const resolved = dry.resolutions[0];
    const hit = resolved && resolved.variant_id != null ? findStatus(dry, resolved.variant_id) : null;
    if (!resolved || resolved.status !== 'ok' || !hit) {
      setMessage({ severity: 'error', text: `找不到條碼 ${code}，未加入` });
      feedback('error');
      return;
    }

    // A second code for a variant already listed (NK code vs manufacturer EAN).
    const sameVariant = linesRef.current.find((l) => !l.undone && l.variant_id === hit.item.variant_id);
    if (sameVariant) {
      bump(sameVariant.key, code);
      feedback('ok');
      return;
    }

    let line: Line = {
      key: newKey(),
      variant_id: hit.item.variant_id,
      code,
      codes: [code],
      count: 1,
      status: hit.status,
      item: hit.item,
      fromSlot: hit.item.from_slot ?? null,
      moveOffice: hit.status === 'need_office_move',
    };

    // 只新增 saves each scan on the spot (nothing to review — nothing is cleared).
    if (sess.mode === 'add' && hit.status !== 'already' && hit.status !== 'blocked') {
      try {
        const r = await commitAdd(sess, line);
        changedRef.current = true;
        if (sessionRef.current !== sess) return; // saved, but the staffer has moved on
        const updated = [...r.set, ...r.moved_from, ...r.already].find((i) => i.variant_id === line.variant_id);
        line = { ...line, sessionId: r.session_id, item: updated || line.item };
        if (!updated) {
          setMessage({ severity: 'warning', text: `${variantLabel(line.item)} 剛被改動，未套用，請再掃一次` });
          feedback('error');
          return;
        }
      } catch (err: any) {
        setMessage({ severity: 'error', text: errorText(err, '儲存失敗，請再掃一次') });
        feedback('error');
        return;
      }
    }

    setLines((prev) => [line, ...prev]);
    const label = variantLabel(line.item);
    const dest = sess.target === 'display' ? '上架' : sess.slot;
    if (line.status === 'blocked') {
      setMessage({ severity: 'error', text: `${label}：未登記庫存（辦公室與在櫃都是 0），不會套用` });
      feedback('error');
    } else if (line.status === 'moved_from') {
      setMessage({ severity: 'warning', text: `${label} 原本在 ${line.fromSlot}，${sess.mode === 'add' ? '已移到' : '更新後會移到'} ${dest}` });
      feedback('warn');
    } else if (line.status === 'need_office_move') {
      setMessage({
        severity: 'warning',
        text: sess.mode === 'add'
          ? `${label}：在櫃數量已更正（${stockFixText(line.item) || '已設定'}）`
          : `${label}：在櫃 ${line.item.qty_on_hand ?? 0} 件，確認時會自動${stockFixText(line.item)}`,
      });
      feedback('warn');
    } else {
      setMessage(null);
      feedback('ok');
    }
  };

  const enqueue = (code: string) => {
    setPending((n) => n + 1);
    queueRef.current = queueRef.current
      .then(() => processCode(code))
      .catch(() => {})
      .finally(() => setPending((n) => Math.max(0, n - 1)));
  };

  const handleCode = (raw: string, fromManual = false) => {
    const code = String(raw || '').trim();
    if (!code) return;
    if (!fromManual) {
      const now = Date.now();
      if (lastScan.current.code === code && now - lastScan.current.at < SAME_CODE_MS) return;
      lastScan.current = { code, at: now };
    }

    const slot = parseSlotBarcode(code);
    if (slot) {
      if (phase === 'setup') {
        setTarget('slot');
        setSlotInput(slot);
        setMessage({ severity: 'success', text: `已選擇櫃位 ${slot}` });
        feedback('ok');
        return;
      }
      const sess = sessionRef.current;
      if (sess && sess.target === 'slot' && sess.slot === slot) {
        setMessage({ severity: 'info', text: `正在盤點 ${slot}` });
        feedback('ok');
        return;
      }
      // Never switch silently: a shelf label scanned mid-session is either a
      // slip or a real move to the next shelf, and only the staffer knows which.
      setSwitchTo(slot);
      feedback('warn');
      return;
    }

    if (phase !== 'scan') {
      setMessage({ severity: 'warning', text: '請先選好盤點目標，按「開始掃描」後再掃商品' });
      feedback('error');
      return;
    }
    enqueue(code);
  };

  useBarcodeScanner({ onScan: (c) => handleCode(c), enabled: open && (phase === 'setup' || phase === 'scan') && !switchTo });

  const undoLine = async (line: Line) => {
    if (!line.sessionId) return;
    setBusy(true);
    try {
      const r: UndoResult = (await api.post(`/api/noke/inventory/reconcile/${line.sessionId}/undo`)).data;
      changedRef.current = true;
      setLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, undone: true } : l)));
      setMessage(r.skipped.length
        ? { severity: 'warning', text: `已復原，但：${r.skipped.map((s) => s.reason).join('；')}` }
        : { severity: 'success', text: `已復原 ${variantLabel(line.item)}` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: errorText(err, '復原失敗') });
    } finally {
      setBusy(false);
    }
  };

  // ---- review / commit (full mode) -------------------------------------------
  const openReview = async (empty = false) => {
    const sess = sessionRef.current;
    if (!sess) return;
    if (!empty && linesRef.current.every((l) => l.undone)) return;
    setBusy(true);
    setMessage(null);
    try {
      const d: DryRun = (await api.post('/api/noke/inventory/reconcile', {
        target: sess.target,
        slot: sess.slot ?? undefined,
        mode: 'full',
        codes: empty ? [] : linesRef.current.map((l) => l.code),
        dry_run: true,
      })).data;
      setReview(d);
      setEmptyReview(empty);
      setKeepIds(new Set());
      setPhase('review');
    } catch (err: any) {
      setMessage({ severity: 'error', text: errorText(err, '無法產生檢查清單，請再試一次') });
    } finally {
      setBusy(false);
    }
  };

  const plan = useMemo(() => {
    if (!review) return null;
    const removeIds = review.remove_candidates.filter((r) => !keepIds.has(r.variant_id)).map((r) => r.variant_id);
    // Count corrections are always applied server-side; ids still sent for older servers.
    const moveIds = review.need_office_move.map((r) => r.variant_id);
    return { removeIds, moveIds, setCount: review.set.length + review.moved_from.length + moveIds.length };
  }, [review, keepIds]);

  const confirmCommit = async () => {
    const sess = sessionRef.current;
    if (!sess || !plan) return;
    const body = {
      target: sess.target,
      slot: sess.slot ?? undefined,
      mode: 'full' as const,
      codes: emptyReview ? [] : linesRef.current.map((l) => l.code),
      move_from_office_ids: emptyReview ? [] : plan.moveIds,
      remove_ids: plan.removeIds,
      dry_run: false,
      ...(emptyReview ? { confirm_empty: true } : {}),
    };
    const payload = JSON.stringify(body);
    if (!commitRef.current || commitRef.current.payload !== payload) commitRef.current = { ref: newKey(), payload };
    setBusy(true);
    try {
      const r: CommitResult = (await api.post('/api/noke/inventory/reconcile', { ...body, client_ref: commitRef.current.ref })).data;
      changedRef.current = true;
      storageRemove(draftKey(sess.target, sess.slot));
      setResult(r);
      setUndoResult(null);
      setPhase('done');
      feedback('ok');
    } catch (err: any) {
      setMessage({ severity: 'error', text: errorText(err, '更新失敗，請再按一次（不會重複套用）') });
      feedback('error');
    } finally {
      setBusy(false);
    }
  };

  const undoSession = async () => {
    if (!result) return;
    setBusy(true);
    try {
      const r: UndoResult = (await api.post(`/api/noke/inventory/reconcile/${result.session_id}/undo`)).data;
      changedRef.current = true;
      setUndoResult(r);
    } catch (err: any) {
      setMessage({ severity: 'error', text: errorText(err, '復原失敗') });
    } finally {
      setBusy(false);
    }
  };

  const discard = () => {
    const sess = sessionRef.current;
    // eslint-disable-next-line no-alert
    if (lines.length > 0 && !window.confirm(`放棄這次盤點？已掃的 ${lines.length} 款不會套用。`)) return;
    if (sess) storageRemove(draftKey(sess.target, sess.slot));
    setLines([]);
    setPendingSlot(null);
    setSession(null);
    setDrafts(listDrafts());
    setMessage(null);
    setPhase('setup');
  };

  const close = () => onClose(changedRef.current);

  // ---- render ----------------------------------------------------------------
  const setupSlot = parseSlotBarcode(slotInput) ?? normalizeSlot(slotInput);
  const setupDraft = mode === 'full' && (target === 'display' || setupSlot)
    ? drafts.find((d) => draftKey(d.target, d.slot) === draftKey(target, setupSlot)) || null
    : null;
  const canStart = target === 'display' || !!setupSlot;
  const isFull = session?.mode === 'full';
  const accent = phase === 'setup' ? theme.palette.grey[500] : isFull ? theme.palette.error.main : theme.palette.info.main;
  const pieces = lines.filter((l) => !l.undone).reduce((n, l) => n + l.count, 0);
  const activeLines = lines.filter((l) => !l.undone);
  const mustFinish = !!session && session.mode === 'full' && lines.length > 0;
  const fmtTime = (ms: number) => new Date(ms).toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const statusChips = (line: Line, sess: Session) => {
    const chips: React.ReactNode[] = [];
    if (line.undone) chips.push(<Chip key="u" size="small" label="已復原" />);
    else if (line.status === 'blocked') chips.push(<Chip key="b" size="small" color="error" label="未登記庫存" />);
    else if (line.status === 'already') chips.push(<Chip key="a" size="small" variant="outlined" label={sess.target === 'display' ? '已上架' : `已在 ${sess.slot}`} />);
    else if (line.status === 'moved_from') chips.push(<Chip key="m" size="small" color="warning" label={`原本在 ${line.fromSlot}`} />);
    else if (line.status === 'need_office_move') {
      const fix = stockFixText(line.item);
      chips.push(sess.mode === 'add' && line.sessionId
        ? <Chip key="o" size="small" color="warning" label={fix ? `已${fix}` : '數量已更正'} />
        : <Chip key="o" size="small" color="warning" label={`將${fix}`} />);
    }
    else chips.push(<Chip key="s" size="small" color="success" variant="outlined" label="新設定" />);
    if (sess.mode === 'add' && line.sessionId && !line.undone) chips.push(<Chip key="saved" size="small" color="success" label="已儲存" />);
    return chips;
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderTop: `6px solid ${accent}`, py: 1.5 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" noWrap>
            盤點模式{session ? ` · ${targetLabel(session)}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={close} aria-label="關閉"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        {message && (
          <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>
        )}

        {phase === 'setup' && (
          <Stack spacing={2}>
            {drafts.length > 0 && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>繼續上次未完成的盤點</Typography>
                <Stack spacing={1}>
                  {drafts.map((d) => (
                    <Button key={draftKey(d.target, d.slot)} variant="outlined" size="large" fullWidth
                      onClick={() => startSession(d.target, d.slot, 'full', true)}>
                      {targetLabel(d)} · {d.lines.length} 款 · {fmtTime(d.savedAt)}
                    </Button>
                  ))}
                </Stack>
              </Alert>
            )}

            <Typography variant="subtitle2" fontWeight={800}>1. 盤點哪一個</Typography>
            <ToggleButtonGroup exclusive fullWidth value={target}
              onChange={(_e, v: ReconcileTarget | null) => { if (v) setTarget(v); }}>
              <ToggleButton value="display" sx={{ py: 1.5, fontWeight: 700 }}>上架（櫃面）</ToggleButton>
              <ToggleButton value="slot" sx={{ py: 1.5, fontWeight: 700 }}>櫃位</ToggleButton>
            </ToggleButtonGroup>
            {target === 'slot' && (
              <Autocomplete
                freeSolo
                options={NOKE_SLOTS}
                value={slotInput}
                onChange={(_e, v) => setSlotInput(parseSlotBarcode(v) ?? (v || ''))}
                onInputChange={(_e, v) => setSlotInput(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="櫃位"
                    helperText="也可以直接掃櫃位標籤上的條碼"
                    inputProps={{
                      ...params.inputProps,
                      // A label scanned while this field is focused arrives as typed
                      // text + Enter (the window scanner hook stands aside for inputs),
                      // so the field resolves SLOT-xx itself, then hands focus back.
                      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        const parsed = e.key === 'Enter' ? parseSlotBarcode(e.currentTarget.value) : null;
                        if (parsed) {
                          e.preventDefault();
                          (e as any).defaultMuiPrevented = true;
                          setSlotInput(parsed);
                          setMessage({ severity: 'success', text: `已選擇櫃位 ${parsed}` });
                          feedback('ok');
                          releaseFocus();
                          return;
                        }
                        const own = (params.inputProps as { onKeyDown?: (ev: React.KeyboardEvent<HTMLInputElement>) => void }).onKeyDown;
                        if (own) own(e);
                      },
                    }}
                  />
                )}
              />
            )}

            <Typography variant="subtitle2" fontWeight={800}>2. 方式</Typography>
            <ToggleButtonGroup exclusive fullWidth value={mode}
              onChange={(_e, v: ReconcileMode | null) => { if (v) setMode(v); }}>
              <ToggleButton value="full" color="error" sx={{ py: 1.5, fontWeight: 700 }}>全部重盤</ToggleButton>
              <ToggleButton value="add" color="info" sx={{ py: 1.5, fontWeight: 700 }}>只新增</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary">
              {mode === 'full'
                ? '把這一區的每一款都掃一次，最後檢查清單確認。沒掃到的會被移除（確認前可以逐款保留）。'
                : '掃到的立即設定，不會移除任何商品。適合補登幾款。'}
            </Typography>
          </Stack>
        )}

        {phase === 'scan' && session && (
          <>
            {session.mode === 'full' ? (
              <Alert severity="error" variant="filled" sx={{ mb: 2, fontWeight: 700 }}>
                全部重盤：未掃到的會{session.target === 'display' ? '被移除上架' : `移出 ${session.slot}`}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                只新增：掃到的會{session.target === 'display' ? '設為上架' : `放進 ${session.slot}`}，每掃一件立即儲存，不會移除任何商品。
              </Alert>
            )}

            <TextField
              fullWidth
              label="手動輸入條碼（按 Enter）"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const code = manual.trim();
                setManual('');
                // Typing it again is deliberate, so no duplicate guard here.
                if (code) handleCode(code, true);
              }}
              helperText="掃描槍直接掃即可，不用點這個欄位"
              sx={{ mb: 1.5 }}
            />

            <Divider sx={{ mb: 1 }}>
              <Chip size="small" label={`已掃 ${activeLines.length} 款 · ${pieces} 件`} />
              {pending > 0 && <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />}
            </Divider>

            {lines.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                掃第一件吊牌開始盤點「{targetLabel(session)}」
                {session.mode === 'full' && (
                  <Box sx={{ mt: 2 }}>
                    <Button variant="text" size="small" color="inherit" disabled={busy || pending > 0}
                      onClick={() => openReview(true)}>
                      {session.target === 'display' ? '櫃面沒有任何上架商品' : '這個櫃位是空的'}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {lines.map((line) => (
                  <Box key={line.key} sx={{ py: 1, opacity: line.undone ? 0.45 : 1 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Thumb src={line.item.image_url} size={52} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={700} noWrap>{variantLabel(line.item)}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {line.item.product_title}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{statusChips(line, session)}</Box>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>×{line.count}</Typography>
                      {session.mode === 'full' ? (
                        <IconButton aria-label="移除這一筆" onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      ) : line.sessionId && !line.undone ? (
                        <IconButton aria-label="復原" disabled={busy} onClick={() => undoLine(line)}>
                          <UndoIcon />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}

        {phase === 'review' && review && plan && session && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {emptyReview
                ? `沒有掃任何商品：確認後「${targetLabel(session)}」裡的 ${plan.removeIds.length} 款全部會被移除。取消勾選可保留。`
                : `確認後才會寫入。取消勾選「將移除」的商品，就會保留原本的${session.target === 'display' ? '上架' : '櫃位'}。`}
            </Alert>
            <Section title="新設定" count={review.set.length} color={theme.palette.success.main}>
              {review.set.map((it) => <ItemRow key={it.variant_id} item={it} />)}
            </Section>
            <Section title="從其他櫃位移來" count={review.moved_from.length} color={theme.palette.warning.main}>
              {review.moved_from.map((it) => <ItemRow key={it.variant_id} item={it} extra={`原本在 ${it.from_slot}`} />)}
            </Section>
            <Section title="自動更正在櫃數量" count={review.need_office_move.length} color={theme.palette.warning.main}>
              {review.need_office_move.map((it) => <ItemRow key={it.variant_id} item={it} extra={`將${stockFixText(it)}`} />)}
            </Section>
            <Section title="未掃到 → 將移除" count={review.remove_candidates.length} color={theme.palette.error.main}>
              {review.remove_candidates.map((it) => (
                <ItemRow key={it.variant_id} item={it}
                  action={<Checkbox
                    checked={!keepIds.has(it.variant_id)}
                    inputProps={{ 'aria-label': '移除' }}
                    onChange={() => {
                      setKeepIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(it.variant_id)) next.delete(it.variant_id); else next.add(it.variant_id);
                        return next;
                      });
                      releaseFocus();
                    }} />} />
              ))}
            </Section>
            <Section title="未登記庫存（不會套用）" count={review.blocked.length} color={theme.palette.text.secondary}>
              {review.blocked.map((it) => <ItemRow key={it.variant_id} item={it} />)}
            </Section>
            <Section title="已在" count={review.already.length}>
              {review.already.map((it) => <ItemRow key={it.variant_id} item={it} />)}
            </Section>
            {(review.unknown.length > 0 || review.ambiguous.length > 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                無法對應的條碼（不會套用）：{[...review.unknown, ...review.ambiguous].join('、')}
              </Alert>
            )}
          </>
        )}

        {phase === 'done' && result && session && (
          <Stack spacing={1.5}>
            <Alert severity="success">
              已更新「{targetLabel(session)}」：設定 {result.set.length + result.moved_from.length} 款・移除 {result.removed.length} 款
              {result.set.some((i) => i.moved_qty || i.found_qty) ? `・更正數量 ${result.set.filter((i) => i.moved_qty || i.found_qty).length} 款` : ''}
            </Alert>
            {result.remove_skipped.length > 0 && (
              <Alert severity="warning">{result.remove_skipped.length} 款在確認前剛被改動，已保留、沒有移除。</Alert>
            )}
            {result.not_moved.length > 0 && (
              <Alert severity="info">{result.not_moved.length} 款在辦公室、沒勾轉入門市，未設定。</Alert>
            )}
            {result.blocked.length > 0 && (
              <Alert severity="info">{result.blocked.length} 款未登記庫存，未套用。</Alert>
            )}
            {undoResult && (
              <Alert severity={undoResult.skipped.length ? 'warning' : 'success'}>
                已復原這次盤點{undoResult.skipped.length ? `；${undoResult.skipped.length} 款未能復原：${undoResult.skipped.map((s) => s.reason).join('；')}` : ''}
              </Alert>
            )}
          </Stack>
        )}

        {busy && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        {phase === 'setup' && (
          setupDraft ? (
            <>
              <Button size="large" onClick={() => startSession(target, setupSlot, mode, false)}>重新開始</Button>
              <Button size="large" variant="contained" onClick={() => startSession(target, setupSlot, mode, true)}>
                繼續上次（{setupDraft.lines.length} 款）
              </Button>
            </>
          ) : (
            <Button size="large" variant="contained" fullWidth disabled={!canStart}
              onClick={() => startSession(target, setupSlot, mode, false)}>
              開始掃描
            </Button>
          )
        )}
        {phase === 'scan' && session?.mode === 'full' && (
          <>
            <Button size="large" color="inherit" onClick={discard}>放棄</Button>
            <Button size="large" variant="contained" color="error" sx={{ flexGrow: 1 }}
              disabled={busy || pending > 0 || activeLines.length === 0} onClick={() => openReview()}>
              檢查並更新（{activeLines.length} 款）
            </Button>
          </>
        )}
        {phase === 'scan' && session?.mode === 'add' && (
          <Button size="large" variant="contained" fullWidth onClick={close}>完成</Button>
        )}
        {phase === 'review' && plan && (
          <>
            <Button size="large" disabled={busy}
              onClick={() => { setPendingSlot(null); setEmptyReview(false); setPhase('scan'); }}>
              繼續掃描
            </Button>
            <Button size="large" variant="contained" color="error" sx={{ flexGrow: 1 }} disabled={busy} onClick={confirmCommit}>
              {emptyReview
                ? `確定清空：將移除全部 ${plan.removeIds.length} 款`
                : `確認更新（設定 ${plan.setCount}・移除 ${plan.removeIds.length}）`}
            </Button>
          </>
        )}
        {phase === 'done' && (
          <>
            {!undoResult && <Button size="large" startIcon={<UndoIcon />} disabled={busy} onClick={undoSession}>復原這次盤點</Button>}
            {pendingSlot ? (
              <Button size="large" variant="contained" sx={{ flexGrow: 1 }}
                onClick={() => { const s = pendingSlot; setPendingSlot(null); startSession('slot', s, session?.mode || 'full', true); }}>
                接著盤點 {pendingSlot}
              </Button>
            ) : (
              <Button size="large" variant="outlined" onClick={() => { setSession(null); setLines([]); setDrafts(listDrafts()); setPhase('setup'); }}>
                再盤點一區
              </Button>
            )}
            <Button size="large" variant="contained" onClick={close}>完成</Button>
          </>
        )}
      </DialogActions>

      {/* Shelf label scanned mid-session: confirm, never switch silently. A full
          session with scans must finish (review + commit) first — its draft is
          kept either way. */}
      <Dialog open={!!switchTo} onClose={() => setSwitchTo(null)}>
        <DialogTitle>切換到櫃位 {switchTo}？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {mustFinish && session
              ? `「${targetLabel(session)}」還有 ${lines.length} 款尚未更新。請先完成這一區（檢查並更新），完成後可以接著盤點 ${switchTo}。已掃的不會遺失。`
              : session
                ? `目前「${targetLabel(session)}」${session.mode === 'add' ? '已掃的都已儲存' : '還沒有掃到商品'}，切換後改盤點 ${switchTo}。`
                : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="large" onClick={() => setSwitchTo(null)}>取消</Button>
          {mustFinish && session ? (
            <Button size="large" variant="contained" color="error"
              onClick={() => { setPendingSlot(switchTo); setSwitchTo(null); openReview(); }}>
              先完成{targetLabel(session)}
            </Button>
          ) : (
            <Button size="large" variant="contained"
              onClick={() => { const s = switchTo; setSwitchTo(null); if (s) startSession('slot', s, session?.mode || 'full', true); }}>
              切換到 {switchTo}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
